import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { FieldsetModule } from 'primeng/fieldset';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { forkJoin } from 'rxjs';
import { CrmAccount, CrmClient } from '../../crm/crm.model';
import { CrmService } from '../../crm/crm.service';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { warehousingBreadcrumb, warehousingHomeBreadcrumb } from '../warehousing-common';
import { WarehousingBillingDraft } from '../warehousing.model';
import { WarehousingService } from '../warehousing.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { CommercialPartyPickerComponent } from '../../shared/commercial-party-picker/commercial-party-picker.component';
import { CommercialPartyReference } from '../../shared/commercial-party-picker/commercial-party.model';

@Component({
  selector: 'app-warehousing-billing-draft-generate',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, RouterModule, BreadcrumbModule, ButtonModule, CardModule, FieldsetModule,
    IftaLabelModule, InputGroupModule, InputGroupAddonModule, InputTextModule, ProgressBarModule, SelectModule,
    PageFormSectionComponent, PageHeroComponent, PageShellComponent, TextareaModule, ToastModule,
    CommercialPartyPickerComponent
  ],
  providers: [MessageService],
  templateUrl: './warehousing-billing-draft-generate.component.html'
})
export class WarehousingBillingDraftGenerateComponent implements OnInit {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private warehousingService = inject(WarehousingService);
  private crmService = inject(CrmService);
  private session = inject(SessionStorageService);
  commercialPartyReference: CommercialPartyReference = { source: 'CRM' };
  isLoading = false;
  breadcrumbItems: MenuItem[] = warehousingBreadcrumb('Generate Billing Draft', 'Billing Drafts', '/warehousing/billing-drafts');
  homeBreadcrumb: MenuItem = warehousingHomeBreadcrumb();
  clients: CrmClient[] = [];
  accounts: CrmAccount[] = [];

  billingForm = this.fb.group({
    crmClientId: [null as number | null],
    crmAccountId: [null as number | null],
    crmClientContactId: [null as number | null],
    legacyCompanyId: [null as number | null],
    legacyCustomerId: [null as number | null],
    billingStartDate: [this.monthStart(), Validators.required],
    billingEndDate: [this.today(), Validators.required],
    currency: ['SAR'],
    notes: ['']
  });

  ngOnInit(): void {
    forkJoin({ clients: this.crmService.getClients(), accounts: this.crmService.getAccounts() }).subscribe(response => {
      this.clients = response.clients || [];
      this.accounts = response.accounts || [];
    });
  }

  generateBillingDraft(): void {
    if (this.isLoading) {
      return;
    }
    if (this.billingForm.invalid || !this.hasCommercialParty()) {
      this.billingForm.markAllAsTouched();
      if (!this.hasCommercialParty()) {
        this.messageService.add({ severity: 'warn', summary: 'Commercial party', detail: 'Select a CRM client or legacy customer.' });
      }
      return;
    }
    this.isLoading = true;
    this.warehousingService.generateBillingDraft(this.billingForm.getRawValue() as WarehousingBillingDraft).subscribe({
      next: resp => {
        this.isLoading = false;
        if (!resp.success) return;
        this.messageService.add({ severity: 'success', summary: 'Draft Generated', detail: 'Storage billing draft was generated.' });
        this.router.navigate(['/warehousing/billing-drafts/edit', resp.data?.id]);
      },
      error: () => this.isLoading = false
    });
  }

  filteredAccounts(clientId: number | null | undefined): CrmAccount[] {
    return this.accounts.filter(account => !clientId || !account.crmClientId || account.crmClientId === clientId);
  }

  get owningCompanyId(): number | undefined { return this.session.getCompany()?.id; }

  onCommercialPartyChange(reference: CommercialPartyReference): void {
    this.commercialPartyReference = reference;
    this.billingForm.patchValue({
      crmClientId: reference.crmClientId ?? null,
      crmAccountId: reference.crmAccountId ?? null,
      crmClientContactId: reference.crmContactId ?? null,
      legacyCompanyId: reference.legacyCompanyId ?? null,
      legacyCustomerId: reference.legacyCustomerId ?? null
    });
  }

  private hasCommercialParty(): boolean {
    return !!(this.billingForm.value.crmClientId || this.billingForm.value.legacyCompanyId || this.billingForm.value.legacyCustomerId);
  }

  discard(): void { this.router.navigate(['/warehousing/billing-drafts']); }
  private today(): string { return new Date().toISOString().slice(0, 10); }
  private monthStart(): string {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
  }
}
